import type { Question } from '@harness/shared/protocol';
import { useState } from 'react';
import styles from '../styles/cards.module.css';
import { TEXT } from '../texts';

interface Choice {
  labels: string[];
  own: string | null;
}

function answerOf(choice: Choice | undefined): string {
  if (choice === undefined) return '';
  return choice.own !== null ? choice.own.trim() : choice.labels.join(', ');
}

interface QuestionCardProps {
  questions: Question[];
  closed: boolean;
  onAnswer: (answers: Record<string, string>) => void;
}

/** AskUserQuestion of the harness: options as buttons, multi-select as checkboxes, or an own answer. */
export function QuestionCard({ questions, closed, onAnswer }: QuestionCardProps): React.JSX.Element {
  const [choices, setChoices] = useState<Record<string, Choice>>({});
  const [sent, setSent] = useState<Record<string, string> | null>(null);

  const submit = (next: Record<string, Choice>): void => {
    const answers = Object.fromEntries(questions.map((question) => [question.question, answerOf(next[question.question])]));
    setSent(answers);
    onAnswer(answers);
  };

  if (closed || sent !== null) {
    return (
      <div className={styles.closed}>
        {TEXT.answered}
        {sent !== null && `: ${Object.values(sent).join(' · ')}`}
      </div>
    );
  }

  const single = questions.length === 1 && questions[0]?.multiSelect === false;
  const complete = questions.every((question) => answerOf(choices[question.question]) !== '');
  const update = (question: Question, choice: Choice): Record<string, Choice> => {
    const next = { ...choices, [question.question]: choice };
    setChoices(next);
    return next;
  };

  return (
    <div className={styles.card} role="group" aria-label={questions.map((question) => question.header).join(', ')}>
      {questions.map((question) => {
        const choice = choices[question.question] ?? { labels: [], own: null };
        return (
          <fieldset key={question.question} className={styles.question}>
            <legend className={styles.header}>{question.header}</legend>
            <div className={styles.title}>{question.question}</div>
            <div className={styles.options}>
              {question.options.map((option) =>
                question.multiSelect ? (
                  <label key={option.label} className={`${styles.option} ${styles.check}`}>
                    <input
                      type="checkbox"
                      checked={choice.labels.includes(option.label)}
                      onChange={(event) =>
                        update(question, {
                          own: null,
                          labels: event.target.checked ? [...choice.labels, option.label] : choice.labels.filter((label) => label !== option.label),
                        })
                      }
                    />
                    <span>
                      {option.label}
                      {option.description !== '' && <span className={styles.optionDescription}>{option.description}</span>}
                    </span>
                  </label>
                ) : (
                  <button
                    key={option.label}
                    type="button"
                    className={styles.option}
                    aria-pressed={choice.labels.includes(option.label)}
                    onClick={() => {
                      const next = update(question, { own: null, labels: [option.label] });
                      if (single) submit(next);
                    }}
                  >
                    {option.label}
                    {option.description !== '' && <span className={styles.optionDescription}>{option.description}</span>}
                  </button>
                ),
              )}
              {choice.own === null && (
                <button type="button" className={styles.option} onClick={() => update(question, { labels: [], own: '' })}>
                  {TEXT.ownAnswer}
                </button>
              )}
            </div>
            {choice.own !== null && (
              <input
                className={styles.input}
                aria-label={`${TEXT.ownAnswer}: ${question.question}`}
                placeholder={TEXT.ownAnswerPlaceholder}
                value={choice.own}
                autoFocus
                onChange={(event) => update(question, { labels: [], own: event.target.value })}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' && single && answerOf(choices[question.question]) !== '') submit(choices);
                }}
              />
            )}
          </fieldset>
        );
      })}
      {(!single || Object.values(choices).some((choice) => choice.own !== null)) && (
        <div className={styles.actions}>
          <button type="button" className={styles.primary} disabled={!complete} onClick={() => submit(choices)}>
            {TEXT.answer}
          </button>
        </div>
      )}
    </div>
  );
}
